import { create } from 'zustand'

import {
  getAIBundleDescription,
  getAIProductPhotosList,
} from '@flecto/api/shared'
import { type CoreBundleItemPayload } from '@flecto/types'
import { uploadImageByUrl } from '@flecto/utils'

import {
  GRID_SIZE,
  useGridItemPhotosManagerState,
} from '../components/GridItemPhotosManager/useGridItemPhotosManagerState'

type LoadingState = {
  all: boolean
  description: boolean
  images: boolean
  single: boolean
}

export type BundleGeneratorAIState = {
  isLoading: LoadingState
  isLoadingDescription: boolean
  isLoadingImageList: boolean
  imagesLoading: number[]
  abortController: AbortController | null

  setAbortController: (controller: AbortController | null) => void
  cancelGenerateAI: () => void
  isLoadingImageSingle: (index: number) => boolean
  setIsLoadingImageSingle: (index: number, loading: boolean) => void

  getDescriptionWithAI: (
    items: Array<CoreBundleItemPayload>,
    onUpdateContent?: (message: string) => void,
    onSuccess?: () => void
  ) => void
  getImageWithAI: (
    items: Array<CoreBundleItemPayload>,
    index: number
  ) => Promise<{ id: string; urlTemporary: string }>
  getImageListWithAI: (
    items: Array<CoreBundleItemPayload>
  ) => Promise<{ id: string; urlTemporary: string }[]>
  generateListPhotosWithAI: (
    items: Array<CoreBundleItemPayload>
  ) => Promise<void>
}

export const useBundleGeneratorAI = create<BundleGeneratorAIState>(
  (set, get) => {
    let offset = 0

    /** Helper to update loading states */
    const updateLoading = (
      updates: Partial<Omit<BundleGeneratorAIState, 'isLoading'>>
    ) => set(updates)

    return {
      isLoading: {
        get all() {
          return get().isLoadingDescription || get().isLoadingImageList
        },
        get description() {
          return get().isLoadingDescription
        },
        get images() {
          return get().isLoadingImageList
        },
        get single() {
          return get().imagesLoading.length > 0
        },
      },

      isLoadingDescription: false,
      isLoadingImageList: false,
      imagesLoading: [],
      abortController: null,

      setAbortController: (controller) =>
        updateLoading({ abortController: controller }),

      cancelGenerateAI: () => {
        const abortController = get().abortController
        if (abortController) {
          abortController.abort()
          updateLoading({
            isLoadingDescription: false,
            isLoadingImageList: false,
            imagesLoading: [],
            abortController: null,
          })
        }
      },

      isLoadingImageSingle: (index) => get().imagesLoading.includes(index),

      setIsLoadingImageSingle: (index, loading) => {
        const current = get().imagesLoading
        const updated = loading
          ? current.includes(index)
            ? current
            : [...current, index]
          : current.filter((i) => i !== index)
        updateLoading({ imagesLoading: updated })
      },

      getDescriptionWithAI: (items, onUpdateContent, onSuccess) => {
        const abortController = get().abortController || new AbortController()
        updateLoading({ isLoadingDescription: true, abortController })

        getAIBundleDescription(items, {
          signal: abortController.signal,
          onmessage: (event) => onUpdateContent?.(event.data as string),
          onclose: () => {
            updateLoading({ isLoadingDescription: false })
            onSuccess?.()
          },
          onerror: () => {
            updateLoading({ isLoadingDescription: false })
            throw new Error('Error getting bundle description with AI.')
          },
        }).catch(() => {
          updateLoading({ isLoadingDescription: false })
          throw new Error('Error getting bundle description with AI.')
        })
      },

      getImageWithAI: async (items, index) => {
        const abortController = get().abortController || new AbortController()
        const { images, total } = await getAIProductPhotosList(
          { items },
          {
            signal: abortController.signal,
            offset,
            limit: GRID_SIZE,
          }
        )

        offset = total && total > offset ? offset + 1 : 1
        const imageUrl = images[index] || images[0]
        return imageUrl
          ? uploadImageByUrl(imageUrl, abortController.signal)
          : { id: '', urlTemporary: '' }
      },

      getImageListWithAI: async (items) => {
        const abortController = get().abortController || new AbortController()
        const { images, total } = await getAIProductPhotosList(
          { items },
          {
            signal: abortController.signal,
            offset,
            limit: GRID_SIZE,
          }
        )

        offset = total && total > offset ? offset + GRID_SIZE : GRID_SIZE

        const promises = images
          .slice(0, GRID_SIZE)
          .map((url) => uploadImageByUrl(url, abortController.signal))

        return Promise.all(promises)
      },

      generateListPhotosWithAI: (itemIds) =>
        useGridItemPhotosManagerState
          .getState()
          .generateListPhotosWithAI(itemIds),
    }
  }
)
